'use client';
import { ComponentProps, useId, useState, ReactNode, JSX, ChangeEvent, FocusEvent } from 'react';
import styles from './Input.module.scss';
import cn from 'classnames';
import { InvalidFeedback } from '../InvalidFeedback/InvalidFeedback';
import calcLength from '@/packages/string/calcLength';

export interface TextareaProps extends ComponentProps<'textarea'> {
    label: ReactNode;
    showCount?: boolean;
    invalid?: FieldValidityState;
    invalidMessage?: ReactNode;
    maxLength?: number;
    dataCy?: string;
}

export function Textarea({
    label,
    showCount = false,
    invalid = 'untouched',
    invalidMessage,
    maxLength = 120,
    id,
    value,
    onChange,
    onBlur,
    dataCy,
    ...otherProps
}: TextareaProps): JSX.Element {
    const [state, setState] = useState({
        isFocused: false,
        isFilled: Boolean(value),
        value: value || '',
    });

    const [count, setCount] = useState<number>(maxLength);

    const setIsFocused = (focused: boolean) => setState({ ...state, isFocused: focused });

    const setValue = (newValue: string) => setState({ ...state, value: newValue, isFilled: Boolean(newValue) });

    const handleFocus = () => setIsFocused(true);

    const setLength = (maxLength: number, currLength: number) => setCount(calcLength(maxLength, currLength));

    const generatedId = useId();
    const elementId = id ? id : generatedId;
    const errorId = `${elementId}-error`;

    const handleChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        setValue(newValue);

        if (showCount) {
            setLength(maxLength, newValue.length);
        }

        if (onChange) {
            onChange(event);
        }
    };

    function handleBlur(event: FocusEvent<HTMLTextAreaElement, Element>) {
        setIsFocused(false);

        if (onBlur) {
            onBlur(event);
        }
    }

    const inputContainerClassNames = cn(styles.inputContainer, styles.textareaContainer, {
        [styles.active]: state.isFocused,
        [styles.filled]: state.isFilled,
        [styles.invalid]: invalid === 'invalid',
        [styles.noSpacing]: showCount,
    });

    return (
        <>
            {invalid && invalidMessage && (
                <InvalidFeedback validState={invalid} errorId={errorId}>
                    {invalidMessage}
                </InvalidFeedback>
            )}
            <div className={inputContainerClassNames}>
                <label htmlFor={elementId}>{label}</label>
                <textarea
                    {...otherProps}
                    id={elementId}
                    value={value}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    onChange={handleChange}
                    aria-describedby={errorId}
                    data-cy={dataCy}
                />
            </div>
            {showCount && <div className={styles.textareaCount}>{count} characters remaining</div>}
        </>
    );
}
